/**
 * import-csv-chip — pure resolver for the `contacts.import-csv` quick-action
 * chip (#4689).
 *
 * The agent emits this chip after a CSV/VCF upload; clicking it must open the
 * in-app importer pre-loaded with the file the user just sent. The File itself
 * lives only in `recentlySentFilesRef` (a client-side Map keyed by attachment
 * id) — it is NOT carried to the agent, so if the ref was cleared (reload,
 * navigation, a later send) the File is gone and there is nothing to import.
 *
 * Previously the missing-File case showed a transient error toast and returned,
 * which — coupled with the chip's own "Opening the contacts importer…" message —
 * read as a success even though nothing was imported (the #4689 false-confirm
 * bug, frontend surface). This resolver makes the decision explicit and pure so
 * the missing-File branch can be unit-tested: it NEVER implies success and
 * always tells the user the file must be re-attached.
 *
 * Pure: no React, no I/O. MessageList performs the side effects.
 */

/** Outcome of resolving an `contacts.import-csv` chip click. */
export type ImportCsvChipResult =
  | { kind: 'open'; file: File }
  | { kind: 'reattach'; message: string };

/** A clicked chip, as much of it as this resolver needs. */
export interface ImportChipLike {
  kind?: string;
  label?: string;
}

const IMPORT_VERB = /\b(import|add|upload|bring\s*in|load|sync)\b/i;
const CONTACTS_NOUN = /\bcontacts?\b/i;

/**
 * Is this clicked chip an "import the attached file into Contacts" chip?
 *
 * The agent is *supposed* to emit `kind:"contacts.import-csv"`, but LLM
 * compliance with an exact machine-readable field is unreliable — a dropped or
 * mangled `kind` made the click fall through to the default `sendMessage(...)`
 * path, which sent "Opening the contacts importer…" to the agent (told it
 * cannot import) and left the chat holding a non-confirmation instead of
 * opening the importer. So we ALSO accept a label-shaped match
 * (import-verb + contacts-noun, e.g. "Yes, import to Contacts" /
 * "Add to Contacts") as a fallback. The MessageList caller only honors the
 * label-only match when a CSV/VCF file is actually in hand, so a look-alike
 * chip with no attachment is never hijacked.
 */
export function isImportContactsChip(action: ImportChipLike | undefined | null): boolean {
  if (action?.kind === 'contacts.import-csv') return true;
  const label = typeof action?.label === 'string' ? action.label : '';
  return IMPORT_VERB.test(label) && CONTACTS_NOUN.test(label);
}

/** Unmistakable re-attach copy — states plainly that nothing was imported. */
export const IMPORT_CSV_REATTACH_MESSAGE =
  "I couldn't find the uploaded file, so nothing was imported. Re-attach the CSV/VCF and tap “Yes, import to Contacts” to try again.";

/**
 * Resolve a `contacts.import-csv` chip click against the recently-sent files.
 * Returns `open` with the first CSV/VCF File found, or `reattach` with the
 * no-success prompt when the File is no longer available.
 */
export function resolveImportCsvChip(
  recentlySentFiles: Iterable<[unknown, File]>,
): ImportCsvChipResult {
  for (const [, f] of recentlySentFiles) {
    if (/\.(csv|vcf)$/i.test(f.name)) return { kind: 'open', file: f };
  }
  return { kind: 'reattach', message: IMPORT_CSV_REATTACH_MESSAGE };
}
